import { useEffect, useRef } from 'react'
import { logger } from '../../lib/logger'

const CHATWOOT_BASE_URL = import.meta.env.VITE_CHATWOOT_BASE_URL
const CHATWOOT_WEBSITE_TOKEN = import.meta.env.VITE_CHATWOOT_WEBSITE_TOKEN
const CHATWOOT_SCRIPT_ID = 'cleverlohn-chatwoot-sdk'
const CHATWOOT_READY_TIMEOUT_MS = 5000

// Internal Chatwoot SDK class names for its holder nodes. These are not a public
// API, so an SDK upgrade that renames them would disable the mount gate. Kept in
// one place so such a rename is a single edit.
const CHATWOOT_WIDGET_HOLDER_SELECTORS = [
  '.woot-widget-holder',
  '.woot--bubble-holder',
] as const

// A hide/show issued before the holder nodes exist would silently no-op. We
// retry on the next animation frame until they mount, capped so a widget that
// never loads cannot retry forever (~1s at 60fps).
const CHATWOOT_VISIBILITY_RETRY_FRAMES = 60

type ChatwootSDK = {
  run: (config: { websiteToken: string; baseUrl: string }) => void
}

type ChatwootWidget = {
  reset?: () => void
  toggle?: (state?: 'open' | 'close') => void
  toggleBubbleVisibility?: (state: 'show' | 'hide') => void
  // Sets CONTACT custom attributes (e.g. cleverlohn_company_id). Note this
  // targets the contact, not the conversation — Chatwoot's separate
  // setConversationCustomAttributes would need an already-created conversation,
  // which a first message doesn't have. With per-(user, company) widget identity
  // each contact maps to one company, so the operator inbox resolves the
  // conversation's company from this contact attribute (see getCompanyUid).
  setCustomAttributes?: (attributes: Record<string, unknown>) => void
  // Identifies the contact. A distinct identifier yields a distinct Chatwoot
  // contact (and conversation); `identifier_hash` is the HMAC for identity
  // validation. Used to keep each (user, company) chat separate.
  setUser?: (
    identifier: string,
    attributes: {
      identifier_hash?: string
      name?: string
      email?: string
    }
  ) => void
}

declare global {
  interface Window {
    chatwootSDK?: ChatwootSDK
    $chatwoot?: ChatwootWidget
    chatwootSettings?: {
      hideMessageBubble?: boolean
    }
  }
}

let loadPromise: Promise<void> | null = null
let hasRun = false
let activeConsumers = 0

function resetChatwootRunState() {
  hasRun = false
}

// Chatwoot's sdk.js mutates its widget-holder DOM nodes directly. During fast
// navigation the global `$chatwoot` object can exist before (or after) those
// holder elements are in the DOM, so its own toggle/reset helpers throw
// "Cannot read properties of null (reading 'classList')". Our optional chaining
// only guards the method reference, not that internal null deref. When such a
// call runs inside a React effect cleanup the throw escapes into the commit
// phase and trips the global error boundary, which renders the generic error
// page until a reload (CL-WEBAPP-K5). Widget visibility is best-effort, so
// swallow the third-party failure and log it instead.
function safeChatwootCall(action: () => void) {
  try {
    action()
  } catch (error) {
    logger.warn('Chatwoot widget call failed', { error })
  }
}

// Chatwoot's bubble toggle reads `.classList` on its holder nodes. Those nodes
// are created asynchronously after the SDK runs, so toggling before they mount
// is the actual null deref. Gate the toggle on both holders being present.
function isChatwootWidgetMounted(): boolean {
  return (
    typeof document !== 'undefined' &&
    CHATWOOT_WIDGET_HOLDER_SELECTORS.every(
      (selector) => document.querySelector(selector) !== null
    )
  )
}

// True while the requested visibility still matches the active consumer count.
// Prevents a queued retry from hiding the bubble after a new consumer mounted
// (or showing it after the last one unmounted) during fast navigation.
function isVisibilityStillIntended(state: 'show' | 'hide'): boolean {
  return state === 'hide' ? activeConsumers <= 0 : activeConsumers > 0
}

function setChatwootBubbleVisibility(
  state: 'show' | 'hide',
  retriesLeft = CHATWOOT_VISIBILITY_RETRY_FRAMES
) {
  if (isChatwootWidgetMounted()) {
    safeChatwootCall(() => window.$chatwoot?.toggleBubbleVisibility?.(state))
    return
  }

  // Holders not in the DOM yet. Without this retry a hide issued during
  // navigation would never apply, leaving the bubble visible on routes that
  // never mount useChatwoot (e.g. /operations) until reload (CL-WEBAPP-K5).
  if (retriesLeft <= 0 || typeof requestAnimationFrame === 'undefined') {
    return
  }

  requestAnimationFrame(() => {
    if (!isVisibilityStillIntended(state)) {
      return
    }
    setChatwootBubbleVisibility(state, retriesLeft - 1)
  })
}

function getChatwootConfig() {
  if (!CHATWOOT_BASE_URL || !CHATWOOT_WEBSITE_TOKEN) {
    throw new Error(
      'Chatwoot is enabled, but VITE_CHATWOOT_BASE_URL or VITE_CHATWOOT_WEBSITE_TOKEN is missing'
    )
  }

  return {
    baseUrl: CHATWOOT_BASE_URL.replace(/\/$/, ''),
    websiteToken: CHATWOOT_WEBSITE_TOKEN,
  }
}

function runChatwoot() {
  if (hasRun) {
    setChatwootBubbleVisibility('show')
    return
  }

  const { baseUrl, websiteToken } = getChatwootConfig()
  if (typeof window.chatwootSDK?.run !== 'function') {
    throw new Error('Chatwoot SDK loaded, but run() is unavailable')
  }

  window.chatwootSDK.run({
    websiteToken,
    baseUrl,
  })
  hasRun = true
}

function waitForChatwootReady(): Promise<ChatwootWidget> {
  if (window.$chatwoot?.toggle) {
    return Promise.resolve(window.$chatwoot)
  }

  return new Promise((resolve, reject) => {
    const cleanup = () => {
      window.removeEventListener('chatwoot:ready', onReady)
      window.clearTimeout(timeout)
    }
    const onReady = () => {
      if (!window.$chatwoot?.toggle) {
        cleanup()
        reject(
          new Error('Chatwoot ready event fired, but toggle() is unavailable')
        )
        return
      }

      cleanup()
      resolve(window.$chatwoot)
    }
    const timeout = window.setTimeout(() => {
      cleanup()
      reject(new Error('Timed out waiting for Chatwoot widget to become ready'))
    }, CHATWOOT_READY_TIMEOUT_MS)

    window.addEventListener('chatwoot:ready', onReady)
  })
}

function loadChatwoot(): Promise<void> {
  let baseUrl: string
  try {
    baseUrl = getChatwootConfig().baseUrl
  } catch (error) {
    return Promise.reject(error)
  }

  window.chatwootSettings = {
    ...window.chatwootSettings,
    hideMessageBubble: false,
  }

  if (window.chatwootSDK) {
    try {
      runChatwoot()
      return Promise.resolve()
    } catch (error) {
      loadPromise = null
      resetChatwootRunState()
      window.chatwootSDK = undefined
      document.getElementById(CHATWOOT_SCRIPT_ID)?.remove()
      return Promise.reject(error)
    }
  }

  if (loadPromise) {
    return loadPromise
  }

  loadPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(CHATWOOT_SCRIPT_ID)
    if (existingScript) {
      const onLoad = () => {
        existingScript.removeEventListener('load', onLoad)
        existingScript.removeEventListener('error', onError)
        try {
          runChatwoot()
          resolve()
        } catch (error) {
          loadPromise = null
          resetChatwootRunState()
          window.chatwootSDK = undefined
          existingScript.remove()
          reject(error)
        }
      }
      const onError = (error: Event) => {
        existingScript.removeEventListener('load', onLoad)
        existingScript.removeEventListener('error', onError)
        loadPromise = null
        resetChatwootRunState()
        reject(error)
      }

      existingScript.addEventListener('load', onLoad)
      existingScript.addEventListener('error', onError)
      return
    }

    const script = document.createElement('script')
    const firstScript = document.getElementsByTagName('script')[0]
    const scriptParent = firstScript?.parentNode ?? document.head

    script.id = CHATWOOT_SCRIPT_ID
    script.src = `${baseUrl}/packs/js/sdk.js`
    script.async = true
    script.onload = () => {
      script.onload = null
      script.onerror = null
      try {
        runChatwoot()
        resolve()
      } catch (error) {
        loadPromise = null
        resetChatwootRunState()
        window.chatwootSDK = undefined
        script.remove()
        reject(error)
      }
    }
    script.onerror = (error) => {
      script.onload = null
      script.onerror = null
      loadPromise = null
      resetChatwootRunState()
      script.remove()
      reject(error)
    }

    if (firstScript) {
      scriptParent.insertBefore(script, firstScript)
    } else {
      scriptParent.appendChild(script)
    }
  })

  return loadPromise
}

// Per-(user, company) contact identity for the widget. A new `identifier`
// switches the Chatwoot contact, so each company gets its own conversation.
// `identifierHash` is only needed if the inbox enforces identity validation;
// it's optional so the common case needs no server-side HMAC.
export interface ChatwootWidgetIdentity {
  identifier: string
  identifierHash?: string
  name?: string
  email?: string
}

interface UseChatwootOptions {
  // Contact custom attributes applied once the widget is ready — e.g.
  // `{ cleverlohn_company_id, operator_* }` so operator-side company scoping
  // works. Pass null/undefined to set nothing (e.g. admin with no company).
  customAttributes?: Record<string, unknown> | null
  // Identifies the contact per (user, company). When the identifier changes the
  // widget resets and re-identifies, so switching company switches the chat
  // instead of mixing companies into one thread. Null = leave anonymous.
  identity?: ChatwootWidgetIdentity | null
}

export function useChatwoot(enabled: boolean, options?: UseChatwootOptions) {
  useEffect(() => {
    if (!enabled) {
      return undefined
    }

    activeConsumers += 1
    void loadChatwoot().catch((error) => {
      logger.warn('Failed to load Chatwoot widget', { error })
    })

    return () => {
      activeConsumers -= 1

      if (activeConsumers <= 0) {
        activeConsumers = 0
        resetChatwootRunState()
        safeChatwootCall(() => window.$chatwoot?.reset?.())
        setChatwootBubbleVisibility('hide')
      }
    }
  }, [enabled])

  // Identify the contact (per user+company) and tag the conversation once the
  // widget is ready, re-applying when either changes. Kept separate from the
  // consumer-lifecycle effect above so this never resets the shared
  // load/visibility state. Stable serializations are the dependency; the latest
  // values are read from refs so we don't reapply on identity-only re-renders.
  // Best-effort: failures are swallowed/logged, never thrown.
  const customAttributes = options?.customAttributes
  const serializedAttributes =
    customAttributes && Object.keys(customAttributes).length > 0
      ? JSON.stringify(customAttributes)
      : null
  const customAttributesRef = useRef(customAttributes)
  customAttributesRef.current = customAttributes

  const identity = options?.identity
  const serializedIdentity = identity ? JSON.stringify(identity) : null
  const identityRef = useRef(identity)
  identityRef.current = identity
  // The identifier we've already applied to the widget, so an attribute-only
  // change doesn't reset the conversation and a switch only resets once.
  const appliedIdentifierRef = useRef<string | null>(null)

  useEffect(() => {
    // Run when there's something to apply OR a previously-applied identity to
    // clear — otherwise a transition to no-identity (e.g. company deselected)
    // would leave the widget identified as the previous contact.
    if (
      !enabled ||
      (!serializedAttributes &&
        !serializedIdentity &&
        appliedIdentifierRef.current === null)
    ) {
      return undefined
    }

    let cancelled = false
    void loadChatwoot()
      .then(waitForChatwootReady)
      .then(() => {
        if (cancelled) {
          return
        }

        const currentIdentity = identityRef.current
        if (currentIdentity) {
          const identifierChanged =
            appliedIdentifierRef.current !== currentIdentity.identifier
          // Switching to a different contact: reset the current session first
          // so Chatwoot loads the new (user, company) conversation cleanly.
          // Skip the reset on the very first identification (nothing to clear).
          if (identifierChanged && appliedIdentifierRef.current !== null) {
            safeChatwootCall(() => window.$chatwoot?.reset?.())
          }
          // Always (re)identify so name/email updates propagate even when the
          // identifier is unchanged.
          safeChatwootCall(() =>
            window.$chatwoot?.setUser?.(currentIdentity.identifier, {
              // Only sent when the inbox enforces identity validation.
              ...(currentIdentity.identifierHash
                ? { identifier_hash: currentIdentity.identifierHash }
                : {}),
              name: currentIdentity.name,
              email: currentIdentity.email,
            })
          )
          appliedIdentifierRef.current = currentIdentity.identifier
        } else if (appliedIdentifierRef.current !== null) {
          // Identity cleared (e.g. company deselected / logged out): drop back
          // to an anonymous session so the next identification starts clean.
          safeChatwootCall(() => window.$chatwoot?.reset?.())
          appliedIdentifierRef.current = null
        }

        const attributes = customAttributesRef.current
        if (attributes) {
          safeChatwootCall(() =>
            window.$chatwoot?.setCustomAttributes?.(attributes)
          )
        }
      })
      .catch((error) => {
        logger.warn('Failed to identify Chatwoot user', { error })
      })

    return () => {
      cancelled = true
    }
  }, [enabled, serializedAttributes, serializedIdentity])
}

export function openChatwoot() {
  if (window.$chatwoot?.toggle) {
    safeChatwootCall(() => window.$chatwoot?.toggle?.('open'))
    return
  }

  void loadChatwoot()
    .then(waitForChatwootReady)
    .then((chatwoot) => {
      safeChatwootCall(() => chatwoot.toggle?.('open'))
    })
    .catch((error) => {
      logger.warn('Failed to open Chatwoot widget', { error })
    })
}
